import { useBloc as useBlocNext } from "@blac/react";
import { Blac } from "blac-next";
import type { FC } from "react";
import React, { useEffect } from "react";
import { Dialog, Modal as RAModal, ModalOverlay } from "react-aria-components";
import { logAverageTimes } from "src/api/ApiController";
import Language, { Locale } from "src/constants/language";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import { PLATFORM_IS_NATIVE } from "src/hybrid/components/Platform";
import envVariables from "src/lib/envVariables";
import { changeLanguage, getSupportedUserLocale } from "src/lib/i18next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { DeviceTrackingBloc } from "src/state/DeviceTrackingBloc/DeviceTrackingBloc";
import HealthSyncBloc from "src/state/HealthSyncBloc/HealthSyncBloc";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import UserCubit from "src/state/UserCubit/UserCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { toast, useBloc, userState } from "src/state/state";
import {
  AppPopup,
  AppQueryPopupsController
} from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import AppDebugCubit from "./AppDebugCubit";
import styled from "@emotion/styled";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugCategoryHeader,
  DebugContainer,
  DebugDivider,
  DebugFormGroup,
  DebugKeyValue,
  DebugToggle,
  debugVars
} from "./debug.styles";
import DebugActiveProgram from "./DebugActiveProgram";
import DebugAssumeIdentity from "./DebugAssumeIdentity";
import DebugAppointmentScheduler from "./DebugAppointmentScheduler";
import DebugCapInfo from "./DebugCapInfo";
import DebugExperienceGroup from "./DebugExperienceGroup";
import DebugFeatureFlags from "./DebugFeatureFlags";
import DebugChatErrors from "./DebugChatErrors";
import DebugLeapChatFeatures from "./DebugLeapChatFeatures";
import DebugBiometric from "./DebugBiometric";
import DebugBootScreen from "./DebugBootScreen";
import DebugLoadingOverlay from "./DebugLoadingOverlay";
import DebugMeals from "./DebugMeals";
import DebugOverlays from "./DebugOverlays";
import DebugPlatform from "./DebugPlatform";
import DebugPartnerSession from "./DebugPartnerSession";
import DebugInitialLabs from "./DebugInitialLabs";
import DebugSection from "./DebugSection";
import DebugTaskMocker from "./DebugTaskMocker";
import DebugUserJourney from "./DebugUserJourney";
import DebugFileCache from "./DebugFileCache";
import DebugUserStorage from "./DebugUserStorage";
import DebugVersion from "./DebugVersion";

const appDebugCubit = Blac.getBloc(AppDebugCubit);

// Wrappers exist purely so the debug panel joins react-aria's overlay
// stack — when a Modal opens underneath, react-aria inerts the Modal
// instead of marking the debug panel inert and blocking its controls.
const DebugStackOverlay = styled(ModalOverlay)`
  display: contents;
`;

const DebugStackModal = styled(RAModal)`
  display: contents;
`;

const DebugStackDialog = styled(Dialog)`
  display: contents;
  outline: none;
`;

const PrefItem = styled.div`
  padding: 0.5rem 0;
  border-bottom: 1px solid ${debugVars.border};

  &:last-child {
    border-bottom: none;
  }

  .pref-key {
    font-size: 11px;
    font-weight: 600;
    color: ${debugVars.textMuted};
    word-break: break-all;
    margin-bottom: 0.25rem;
  }

  .pref-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .pref-value {
    flex: 1;
    font-family: monospace;
    font-size: ${debugVars.fontSizeSmall};
    word-break: break-word;
    min-width: 0;
  }

  .pref-edit-form {
    display: flex;
    gap: 0.5rem;

    input {
      flex: 1;
      min-width: 0;
    }
  }
`;

const AddPrefForm = styled.div`
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: ${debugVars.bgLight};
  border: 1px solid ${debugVars.border};
  border-radius: ${debugVars.radius};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .add-pref-row {
    display: flex;
    gap: 0.5rem;
  }
`;

const allPreferenceKeys = Object.values(UserPreferenceKeys);

const EditPrefValue: FC<{
  value: string;
  propKey: string;
  onChange: (k: string, v: unknown) => void;
}> = ({ value, propKey, onChange }) => {
  const [editing, setEditing] = React.useState(false);
  const [newValue, setNewValue] = React.useState(value);

  const save = () => {
    if (newValue === "") {
      return;
    }
    try {
      const parsed = JSON.parse(newValue);
      onChange(propKey, parsed);
      setEditing(false);
    } catch {
      toast.error("debug_invalid_json" as never);
    }
  };

  return (
    <PrefItem>
      <div className="pref-key">{propKey}</div>
      {editing ? (
        <div className="pref-edit-form">
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
          />
          <DebugButton className="small" onClick={save}>
            Save
          </DebugButton>
          <DebugButton className="small" onClick={() => setEditing(false)}>
            Cancel
          </DebugButton>
        </div>
      ) : (
        <div className="pref-row">
          <span className="pref-value">{value}</span>
          <DebugButton className="small" onClick={() => setEditing(true)}>
            Edit
          </DebugButton>
        </div>
      )}
    </PrefItem>
  );
};

const AddPreference: FC<{
  existingKeys: string[];
  onAdd: (key: string, value: unknown) => void;
}> = ({ existingKeys, onAdd }) => {
  const [open, setOpen] = React.useState(false);
  const [selectedKey, setSelectedKey] = React.useState("");
  const [value, setValue] = React.useState("");

  const availableKeys = allPreferenceKeys.filter(
    (k) => !existingKeys.includes(k)
  );

  const handleAdd = () => {
    if (!selectedKey || !value) {
      return;
    }
    try {
      const parsed = JSON.parse(value);
      onAdd(selectedKey, parsed);
      setSelectedKey("");
      setValue("");
      setOpen(false);
    } catch {
      toast.error("debug_invalid_json" as never);
    }
  };

  if (!open) {
    return (
      <DebugButton
        className="small"
        onClick={() => setOpen(true)}
        style={{ marginBottom: "0.5rem" }}
      >
        + Add Preference
      </DebugButton>
    );
  }

  return (
    <AddPrefForm>
      <select
        value={selectedKey}
        onChange={(e) => setSelectedKey(e.target.value)}
      >
        <option value="">Select a key...</option>
        {availableKeys.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>
      <div className="add-pref-row">
        <input
          placeholder='JSON value (e.g. "text", true, 42)'
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <DebugButton className="small primary" onClick={handleAdd}>
          Add
        </DebugButton>
        <DebugButton className="small" onClick={() => setOpen(false)}>
          Cancel
        </DebugButton>
      </div>
    </AddPrefForm>
  );
};

const DebugUserDetails: FC = () => {
  const [{ userData }] = useBloc(UserCubit);

  if (!userData) {
    return <p style={{ color: "#666" }}>No user data available</p>;
  }

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Email</span>
        <span className="value">{userData.email || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">User ID</span>
        <span className="value mono" style={{ fontSize: "11px" }}>
          {userData.id || "—"}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Email Verified</span>
        <span className="value">
          {userData.emailVerified ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="danger">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Auth Method</span>
        <span className="value">
          {userData.sessionAuthenticationMethod || "—"}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Password Set</span>
        <span className="value">
          {userData.passwordSet ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="warning">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">MFA Active</span>
        <span className="value">
          {userData.mfaActive ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      {userData.analyticsToken && (
        <DebugKeyValue>
          <span className="key">Analytics</span>
          <span
            className="value mono"
            style={{ fontSize: "10px", wordBreak: "break-all" }}
          >
            {userData.analyticsToken}
          </span>
        </DebugKeyValue>
      )}
    </div>
  );
};

const DebugAuth: FC = () => {
  const [, auth] = useBlocNext(AuthenticationBloc);
  const [bioState, bio] = useBloc(BiometricVerificationBloc);
  const [server, setServer] = React.useState<string | null>();

  React.useEffect(() => {
    void bio.getServer().then((server) => setServer(server));
  }, []);

  return (
    <div>
      <DebugButtonRow>
        <DebugButton className="danger" onClick={() => void auth.logout()}>
          Logout
        </DebugButton>
        <DebugButton onClick={() => void auth.refreshAccessToken()}>
          Refresh Token
        </DebugButton>
      </DebugButtonRow>

      <DebugDivider />

      <h4 style={{ fontSize: "13px", margin: "0 0 0.75rem", fontWeight: 600 }}>
        Biometric Lock
      </h4>
      <DebugKeyValue>
        <span className="key">Enabled</span>
        <span className="value">
          {bioState.isBiometricVerificationEnabled ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Available</span>
        <span className="value">
          {bioState.isBiometricVerificationAvailable ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Type</span>
        <span className="value">{bioState.biometricType || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Server</span>
        <span className="value mono">{server || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Session Verified</span>
        <span className="value">
          {bioState.isSessionVerified ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
    </div>
  );
};

const DebugHealthSync: FC = () => {
  const [healthState, { runBgTask }] = useBloc(HealthSyncBloc);

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Available</span>
        <span className="value">
          {healthState.available ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Steps Auth</span>
        <span className="value">{healthState.stepsAuthorized || "—"}</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Steps Data</span>
        <span className="value">{healthState.stepsData.length} records</span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Last Sync</span>
        <span className="value">
          {healthState.lastImported?.toLocaleString() || "Never"}
        </span>
      </DebugKeyValue>

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton
          onClick={() =>
            AppQueryPopupsController.openPopup(AppPopup.healthSyncSetup)
          }
        >
          Open Setup
        </DebugButton>
        <DebugButton onClick={() => void runBgTask()}>
          Run Background Task
        </DebugButton>
      </DebugButtonRow>
    </div>
  );
};

const DebugDeviceTracking: FC = () => {
  const [deviceState, deviceTracking] = useBlocNext(DeviceTrackingBloc);

  return (
    <div>
      <DebugKeyValue>
        <span className="key">Collected</span>
        <span className="value">
          {deviceState.collected ? (
            <DebugBadge variant="success">Yes</DebugBadge>
          ) : (
            <DebugBadge variant="info">No</DebugBadge>
          )}
        </span>
      </DebugKeyValue>
      <DebugKeyValue>
        <span className="key">Collection Time</span>
        <span className="value">
          {deviceState.collectionTime ? `${deviceState.collectionTime}ms` : "—"}
        </span>
      </DebugKeyValue>
      {deviceState.error && (
        <DebugKeyValue>
          <span className="key">Error</span>
          <span className="value danger">{deviceState.error.message}</span>
        </DebugKeyValue>
      )}

      <DebugButtonRow style={{ marginTop: "0.75rem" }}>
        <DebugButton onClick={() => void deviceTracking.collectAndTrack()}>
          Collect Device Info
        </DebugButton>
      </DebugButtonRow>

      {deviceState.deviceInfo && (
        <details style={{ marginTop: "0.75rem" }}>
          <summary
            style={{ cursor: "pointer", fontSize: "12px", color: "#666" }}
          >
            View Device Info JSON
          </summary>
          <pre
            style={{
              fontSize: "10px",
              background: "#f5f5f7",
              padding: "0.5rem",
              borderRadius: "4px",
              overflow: "auto",
              marginTop: "0.5rem"
            }}
          >
            {JSON.stringify(deviceState.deviceInfo, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

const EnvGroup: FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <h5
      style={{
        fontSize: "11px",
        fontWeight: 600,
        color: "#666",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        margin: "0 0 0.5rem",
        paddingBottom: "0.25rem",
        borderBottom: "1px solid #e0e0e0"
      }}
    >
      {title}
    </h5>
    {children}
  </div>
);

const EnvBooleanRow: FC<{ label: string; value: boolean }> = ({
  label,
  value
}) => (
  <DebugKeyValue>
    <span className="key">{label}</span>
    <span className="value">
      <DebugBadge variant={value ? "success" : "info"}>
        {value ? "true" : "false"}
      </DebugBadge>
    </span>
  </DebugKeyValue>
);

const EnvTextRow: FC<{ label: string; value: string; isUrl?: boolean }> = ({
  label,
  value,
  isUrl
}) => (
  <DebugKeyValue>
    <span className="key">{label}</span>
    <span
      className="value mono"
      style={{
        fontSize: "11px",
        wordBreak: isUrl ? "break-all" : "break-word"
      }}
    >
      {value || "—"}
    </span>
  </DebugKeyValue>
);

const DebugEnvVars: FC = () => {
  return (
    <div>
      <EnvGroup title="Environment">
        <EnvBooleanRow label="PROD" value={envVariables.PROD} />
        <EnvBooleanRow label="DEV" value={envVariables.DEV} />
        <EnvBooleanRow label="DEBUG" value={envVariables.DEBUG} />
        <EnvBooleanRow label="TRACKING" value={envVariables.TRACKING} />
        <EnvTextRow label="ENV_NAME" value={envVariables.ENV_NAME} />
        <EnvTextRow label="APP_ENV" value={envVariables.APP_ENV} />
        <EnvTextRow label="BUILD_VERSION" value={envVariables.BUILD_VERSION} />
      </EnvGroup>

      <EnvGroup title="API Endpoints">
        <EnvTextRow
          label="API_BASE_URL"
          value={envVariables.API_BASE_URL}
          isUrl
        />
        <EnvTextRow
          label="API_BASE_URL_LEGACY"
          value={envVariables.API_BASE_URL_LEGACY}
          isUrl
        />
        <EnvTextRow
          label="WS_BASE_URL"
          value={envVariables.WS_BASE_URL}
          isUrl
        />
        <EnvTextRow label="WEB_URL" value={envVariables.WEB_URL} isUrl />
        <EnvTextRow
          label="CMS_API_BASE_URL"
          value={envVariables.CMS_API_BASE_URL}
          isUrl
        />
        <EnvTextRow
          label="RXDIET_IFRAME_URL"
          value={envVariables.RXDIET_IFRAME_URL}
          isUrl
        />
      </EnvGroup>

      <EnvGroup title="Analytics">
        <EnvTextRow
          label="MIXPANEL_TOKEN"
          value={envVariables.MIXPANEL_TOKEN}
        />
        <EnvTextRow label="SENTRY_DSN" value={envVariables.SENTRY_DSN} isUrl />
        <EnvTextRow
          label="CUSTOMER_IO_SITE_ID"
          value={envVariables.CUSTOMER_IO_SITE_ID}
        />
      </EnvGroup>

      <EnvGroup title="Integrations">
        <EnvTextRow
          label="STRIPE_PUBLISHABLE_KEY"
          value={envVariables.STRIPE_PUBLISHABLE_KEY}
        />
        <EnvTextRow
          label="STRIPE_ACCOUNT_ID"
          value={envVariables.STRIPE_ACCOUNT_ID}
        />
        <EnvTextRow
          label="APPLICATION_ID"
          value={envVariables.APPLICATION_ID}
        />
        <EnvTextRow label="VIMEO_TOKEN" value={envVariables.VIMEO_TOKEN} />
      </EnvGroup>

      <EnvGroup title="Partner SSO">
        <EnvTextRow
          label="SSO_LINK_TRANSCARENT"
          value={envVariables.SSO_LINK_TRANSCARENT}
          isUrl
        />
        <EnvTextRow
          label="TRANSCARENT_DYNAMIC_APP_URL"
          value={envVariables.TRANSCARENT_DYNAMIC_APP_URL}
          isUrl
        />
      </EnvGroup>

      <EnvGroup title="Browser">
        <EnvTextRow label="href" value={window.location.href} isUrl />
        <EnvTextRow label="hostname" value={window.location.hostname} />
      </EnvGroup>
    </div>
  );
};

const AppDebug: FC = () => {
  const [{ show, extra, minified }, cubit] = useBlocNext(AppDebugCubit);
  const [userPref, { updateUserPreferences }] =
    useBlocNext(UserPreferencesCubit);

  const locale = getSupportedUserLocale();

  useEffect(() => {
    cubit.addCustomListeners();
    cubit.init();
    return cubit.removeCustomListeners;
  }, []);

  const handlePrefChange = (k: string, v: unknown) => {
    void updateUserPreferences({ [k]: v }).then(() => {
      toast.success("debug_preference_saved" as never);
    });
  };

  if (!show) {
    return <></>;
  }

  const populate = () => {
    void userState.populateUserprofileWithDemoData();
  };

  const handleChange = (loc: Locale) => {
    void updateUserPreferences({
      [UserPreferenceKeys.language]: loc
    });
    void changeLanguage(loc);
  };

  return (
    <>
      {/* Two copies of the toggle: outside the stack while collapsed (so a
          react-aria Modal underneath doesn't inert it), inside the stack
          while the panel is open (so it stays clickable from within). */}
      {minified && <DebugToggle onClick={cubit.toggleMinified} />}
      <DebugStackOverlay
        isOpen={!minified}
        isDismissable={false}
        isKeyboardDismissDisabled
      >
        <DebugStackModal>
          <DebugStackDialog aria-label="Debug panel">
            <DebugContainer>
              <DebugToggle onClick={cubit.toggleMinified} />
              <DebugButtonRow>
                <DebugButton onClick={() => cubit.closePanel()}>
                  Close Debug
                </DebugButton>
                <DebugButton onClick={() => window.location.reload()}>
                  Reload
                </DebugButton>
              </DebugButtonRow>

              <DebugCategoryHeader>Account</DebugCategoryHeader>
              <DebugSection title="Profile">
                <DebugUserDetails />
              </DebugSection>
              <DebugSection title="Preferences">
                <AddPreference
                  existingKeys={Object.keys(userPref)}
                  onAdd={handlePrefChange}
                />
                {Object.keys(userPref).map((k) => {
                  const val = JSON.stringify(
                    (userPref as unknown as Record<string, unknown>)[k]
                  );
                  return (
                    <EditPrefValue
                      key={k}
                      value={val}
                      propKey={k}
                      onChange={handlePrefChange}
                    />
                  );
                })}
              </DebugSection>
              <DebugSection title="Authentication">
                <DebugAuth />
              </DebugSection>
              {!PLATFORM_IS_NATIVE && (
                <DebugSection title="Assume Identity">
                  <DebugAssumeIdentity />
                </DebugSection>
              )}
              <DebugSection title="Biometric Mock">
                <DebugBiometric />
              </DebugSection>
              <DebugSection title="Local Storage">
                <DebugUserStorage />
              </DebugSection>

              <DebugCategoryHeader>Configuration</DebugCategoryHeader>
              <DebugSection title="Feature Flags">
                <DebugFeatureFlags />
              </DebugSection>
              <DebugSection title="Experience Group">
                <DebugExperienceGroup />
              </DebugSection>
              <DebugSection title="Program Membership">
                <DebugActiveProgram />
              </DebugSection>
              <DebugSection title="Journey Progress">
                <DebugUserJourney />
              </DebugSection>
              <DebugSection title="Partner Mode">
                <DebugPartnerSession />
              </DebugSection>

              <DebugCategoryHeader>Simulations</DebugCategoryHeader>
              <DebugSection title="Initial Labs Tasks">
                <DebugInitialLabs />
              </DebugSection>
              <DebugSection title="Task Simulator">
                <DebugTaskMocker />
              </DebugSection>
              <DebugSection title="Chat Error Simulator">
                <DebugChatErrors />
              </DebugSection>
              <DebugSection title="Chat UI Features">
                <DebugLeapChatFeatures />
              </DebugSection>
              <DebugSection title="Boot Screen">
                <DebugBootScreen />
              </DebugSection>
              <DebugSection title="Loading States">
                <DebugLoadingOverlay />
              </DebugSection>
              <DebugSection title="Overlays & Fullscreen">
                <DebugOverlays />
              </DebugSection>
              <DebugSection title="Meals">
                <DebugMeals />
              </DebugSection>
              <DebugSection title="Appointments">
                <DebugAppointmentScheduler />
              </DebugSection>
              <DebugSection title="Session Dialog">
                <DebugButton
                  onClick={() =>
                    AppQueryPopupsController.openPopup(
                      AppPopup.sessionConfirmation,
                      {
                        additionalParameters: { stay: "false" }
                      }
                    )
                  }
                >
                  Open Session Dialog
                </DebugButton>
              </DebugSection>

              <DebugCategoryHeader>Device & Health</DebugCategoryHeader>
              <DebugSection title="Platform Simulation">
                <DebugPlatform />
              </DebugSection>
              <DebugSection title="HealthKit / Health Connect">
                <DebugHealthSync />
              </DebugSection>
              <DebugSection title="Device Fingerprint">
                <DebugDeviceTracking />
              </DebugSection>
              <DebugSection title="Native App">
                <DebugCapInfo />
              </DebugSection>
              <DebugSection title="OTA Bundles">
                <DebugVersion />
              </DebugSection>

              <DebugCategoryHeader>Environment</DebugCategoryHeader>
              <DebugSection title="Variables">
                <DebugEnvVars />
              </DebugSection>
              <DebugSection title="File Cache">
                <DebugFileCache />
              </DebugSection>
              {Object.keys(extra).length > 0 && (
                <DebugSection title="Custom Data">
                  {Object.keys(extra).map((k) => (
                    <DebugKeyValue key={k}>
                      <span className="key">{k}</span>
                      <span className="value">{String(extra[k])}</span>
                    </DebugKeyValue>
                  ))}
                </DebugSection>
              )}

              <DebugDivider />

              <DebugFormGroup>
                <label>Language</label>
                <select
                  name="language"
                  value={locale}
                  onChange={(e) => handleChange(e.target.value as Locale)}
                  style={{ width: "auto" }}
                >
                  <option value={Locale.en} key={Language.en}>
                    English
                  </option>
                  <option value={Locale.es} key={Language.es}>
                    Español
                  </option>
                </select>
              </DebugFormGroup>

              <DebugDivider />

              <DebugButtonRow>
                <DebugButton onClick={() => logAverageTimes()}>
                  Log API Timings
                </DebugButton>
                <DebugButton
                  className="danger"
                  onClick={() => reportErrorSentry(new Error("Debug Error"))}
                >
                  Test Sentry
                </DebugButton>
                <DebugButton onClick={() => populate()}>
                  Fill Demo Data
                </DebugButton>
              </DebugButtonRow>

              <div style={{ height: "2rem" }} />
            </DebugContainer>
          </DebugStackDialog>
        </DebugStackModal>
      </DebugStackOverlay>
    </>
  );
};

export default AppDebug;

export { appDebugCubit as DebugBlocController };
